/**
 * PG + Firestore dual-read/dual-write for fax `submission_output` artefacts.
 *
 * Postgres column: `submission.submission_output` (PR #4343).
 * Firestore mirror: `medical_pa_orders/{id}/submission_info/output`.
 *
 * Reads are PG-first with Firestore fallback. Writes merge a patch into the
 * existing output (so `filled_pdf_link` is not wiped), then dual-write both
 * stores. Generic submission UPDATE replaces the whole JSONB column, so the
 * merge happens on the client before the PG write.
 *
 * @module faxSubmissionOutputPersistence
 */
import { FirestoreService } from "../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../api/firebase/references";
import { singleOrderDetailsGenericCallForPostgres } from "../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { SingleOrderDetailsGenericUpdateResponse } from "../api/postgreSQL/postCalls/singleOrderDetailsGenericUpdateForPostgres";
import { SingleOrderDetailsTableName } from "../enums/singleOrderDetailsTableName";
import { SubmissionDataResponse } from "../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { logError } from "./customLogger";

/** Fax artefacts stored on `submission.submission_output` / Firestore output doc. */
export interface FaxSubmissionOutput {
  filled_pdf_link?: string;
  fax_details_id?: string | number;
  form_id?: string;
  fax_status?: string;
  fax_status_updated_at?: string;
  submission?: string;
  [key: string]: unknown;
}

/** Result shape returned by `useUpdateOrderDetails` / `useAbortableFetch`. */
type UpdateFetchResult = {
  success: boolean;
  data: SingleOrderDetailsGenericUpdateResponse | null;
  error: unknown;
};

/**
 * Normalizes a raw `submission_output` value into a plain object.
 *
 * @param value - Object or JSON string from PG / API
 * @returns Parsed output object, or empty object when invalid
 */
export const asFaxSubmissionOutput = (value: unknown): FaxSubmissionOutput => {
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }
  return parsed as FaxSubmissionOutput;
};

/**
 * Reads fax output PG-first (`submission.submission_output`), then Firestore.
 *
 * @param orderId - Medical PA order id
 * @param orgId - Tenant org id for the PG read (optional; skips PG when missing)
 * @returns Merged fax output fields from the first store that has any data
 */
export const readFaxSubmissionOutput = async (
  orderId: string,
  orgId?: string | null,
): Promise<FaxSubmissionOutput> => {
  if (orgId) {
    try {
      const response =
        await singleOrderDetailsGenericCallForPostgres<SubmissionDataResponse>({
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.SUBMISSION,
        });
      const pgOutput = asFaxSubmissionOutput(
        response?.submission?.submission_output,
      );
      if (Object.keys(pgOutput).length > 0) {
        return pgOutput;
      }
    } catch (error) {}
  }

  try {
    const firestoreDoc =
      await FirestoreService.getDocument<FaxSubmissionOutput>(
        FirestoreDocumentReference.getFaxInfoPath(orderId),
      );
    const fsOutput = asFaxSubmissionOutput(firestoreDoc);
    return fsOutput;
  } catch (error) {
    logError(error as Error, "Error reading fax submission output");
    return {};
  }
};

/**
 * Merges `patch` into existing fax output and dual-writes Firestore + PG.
 *
 * @param params - Order/org context, patch fields, and PG update runner
 * @returns The merged output that was persisted
 * @throws {Error} When the Firestore write fails, or PG write fails with orgId set
 */
export const dualWriteFaxSubmissionOutput = async (params: {
  orderId: string;
  orgId?: string | null;
  patch: FaxSubmissionOutput;
  updateSubmission: (payload: {
    submission_output: FaxSubmissionOutput;
  }) => Promise<UpdateFetchResult>;
}): Promise<FaxSubmissionOutput> => {
  const { orderId, orgId, patch, updateSubmission } = params;
  const existing = await readFaxSubmissionOutput(orderId, orgId);
  const merged: FaxSubmissionOutput = { ...existing, ...patch };

  await FirestoreService.updateDocument(
    `medical_pa_orders/${orderId}/submission_info/`,
    "output",
    merged,
  );

  if (!orgId) {
    return merged;
  }

  const pgResult = await updateSubmission({ submission_output: merged });

  if (!pgResult.success || pgResult.error) {
    throw (
      pgResult.error ??
      new Error("Failed to persist submission_output to Postgres")
    );
  }
  if (!pgResult.data?.success || (pgResult.data.updated_rows ?? 0) < 1) {
    throw new Error(
      "No submission row updated for submission_output; ensure a submission row exists",
    );
  }

  return merged;
};
